import { Loader2, Lock, Zap } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  createEmptyFlowRuleDraft,
  generateFlowRuleName,
  hasFlowRuleValidationErrors,
  normalizeFlowRuleDraft,
  useCreateFlowRuleMutation,
  useUpdateFlowRuleMutation,
  validateFlowRuleDraft,
  type FlowRule,
  type FlowRuleDraft,
  type FlowRuleValidationErrors,
} from "@/modules/flow";
import { useSidebarData } from "@/modules/mine";
import { useDialog } from "@/shared/components/dialog";
import type { DialogComponentProps } from "@/shared/components/dialog";
import { Button } from "@/shared/ui/button";
import { DialogFooter } from "@/shared/ui/dialog";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Switch } from "@/shared/ui/switch";

import { ActionEditor } from "./action-editor";
import { ConditionEditor } from "./condition-editor";

interface FlowRuleDialogProps extends DialogComponentProps<FlowRule> {
  rule?: FlowRule | null;
}

function toDraft(rule?: FlowRule | null): FlowRuleDraft {
  if (!rule) return createEmptyFlowRuleDraft();
  return {
    name: rule.name,
    enabled: rule.enabled,
    condition_logic: rule.condition_logic,
    conditions: rule.conditions.map((condition) => ({ ...condition, values: condition.values ? [...condition.values] : undefined })),
    actions: rule.actions.map((action) => action.type === "move_to_folder"
      ? { type: "move_to_folder", folder_id: action.folder_id }
      : { type: "add_tags", tag_ids: [...action.tag_ids] }),
    stop_on_match: rule.stop_on_match,
  };
}

export function FlowRuleDialog({
  close,
  resolve,
  rule,
  setBeforeClose,
  setSubmitting,
}: FlowRuleDialogProps) {
  const { confirm } = useDialog();
  const { folders, tags } = useSidebarData();
  const [draft, setDraft] = useState<FlowRuleDraft>(() => toDraft(rule));
  const [initialDraft] = useState<FlowRuleDraft>(() => toDraft(rule));
  const [errors, setErrors] = useState<FlowRuleValidationErrors>({});
  const nameEditedRef = useRef(Boolean(rule));
  const createMutation = useCreateFlowRuleMutation();
  const updateMutation = useUpdateFlowRuleMutation();
  const pending = createMutation.isPending || updateMutation.isPending;

  const descriptionContext = useMemo(() => {
    const folderMap: Record<string, string> = {};
    const visitFolders = (nodes: typeof folders) => {
      nodes.forEach((folder) => {
        folderMap[String(folder.id)] = folder.name;
        if (folder.sub_folders?.length) visitFolders(folder.sub_folders);
      });
    };
    visitFolders(folders);
    return {
      folders: folderMap,
      tags: Object.fromEntries(tags.map((tag) => [String(tag.id), tag.name])),
    };
  }, [folders, tags]);

  useEffect(() => {
    if (nameEditedRef.current) return;
    setDraft((current) => ({
      ...current,
      name: generateFlowRuleName(current, descriptionContext),
    }));
  }, [descriptionContext, draft.actions, draft.conditions]);

  const isDirty = JSON.stringify(normalizeFlowRuleDraft(draft)) !== JSON.stringify(normalizeFlowRuleDraft(initialDraft));

  useEffect(() => {
    setBeforeClose(async () => {
      if (!isDirty) return true;
      return confirm({
        title: "放弃未保存的修改？",
        description: "关闭后，本次修改不会保留。",
        confirmText: "放弃修改",
        variant: "destructive",
      });
    });
    return () => setBeforeClose(undefined);
  }, [confirm, isDirty, setBeforeClose]);

  const handleSave = async () => {
    const nextErrors = validateFlowRuleDraft(draft);
    setErrors(nextErrors);
    if (hasFlowRuleValidationErrors(nextErrors)) {
      requestAnimationFrame(() => {
        document.querySelector("[data-flow-error='true']")?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
      return;
    }

    const payload = normalizeFlowRuleDraft(draft);
    setSubmitting(true);
    try {
      const saved = rule
        ? await updateMutation.mutateAsync({ id: rule.id, data: payload })
        : await createMutation.mutateAsync(payload);
      resolve(saved);
    } catch {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid min-h-0 grid-rows-[minmax(0,1fr)_auto]">
      <div className="min-h-0 flex-1 space-y-7 overflow-y-auto px-6 py-5">
          <div className="space-y-2" data-flow-error={Boolean(errors.name)}>
            <Label htmlFor="flow-name">流程名称</Label>
            <Input
              id="flow-name"
              maxLength={80}
              onChange={(event) => {
                nameEditedRef.current = true;
                setDraft((current) => ({ ...current, name: event.target.value }));
              }}
              value={draft.name}
            />
            {errors.name ? <p className="text-xs text-destructive">{errors.name}</p> : null}
          </div>

          <section className="space-y-3">
            <h3 className="text-base font-semibold">触发器</h3>
            <div className="flex items-center gap-3 rounded-lg bg-muted/30 px-4 py-3">
              <span className="flex size-8 items-center justify-center rounded-md bg-background/70 text-muted-foreground"><Zap className="size-4" /></span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">新收藏创建后</p>
                <p className="mt-0.5 text-xs text-muted-foreground">系统会在收藏信息准备好后自动判断条件。</p>
              </div>
              <Lock className="size-4 text-muted-foreground" />
            </div>
          </section>

          <section className="space-y-3">
            <h3 className="text-base font-semibold">条件</h3>
            <ConditionEditor
              conditions={draft.conditions}
              errors={errors.conditions}
              logic={draft.condition_logic}
              onChange={(conditions) => setDraft((current) => ({ ...current, conditions }))}
              onLogicChange={(condition_logic) => setDraft((current) => ({ ...current, condition_logic }))}
            />
          </section>

          <section className="space-y-3">
            <h3 className="text-base font-semibold">动作</h3>
            <ActionEditor
              actions={draft.actions}
              errors={errors.actions}
              onChange={(actions) => setDraft((current) => ({ ...current, actions }))}
            />
          </section>

          <div className="flex items-start justify-between gap-4 rounded-lg bg-muted/30 px-4 py-3">
            <div>
              <Label htmlFor="stop-on-match">匹配后停止执行后续流程</Label>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">开启后，命中此流程的收藏不会继续匹配排在后面的流程。</p>
            </div>
            <Switch
              checked={draft.stop_on_match}
              id="stop-on-match"
              onCheckedChange={(stop_on_match) => setDraft((current) => ({ ...current, stop_on_match }))}
            />
          </div>
      </div>

      <DialogFooter className="bg-background px-6 pb-5 pt-2">
        <Button disabled={pending} onClick={close} type="button" variant="outline">取消</Button>
        <Button disabled={pending} onClick={() => void handleSave()} type="button">
          {pending ? <Loader2 className="size-4 animate-spin" /> : null}
          {rule ? "保存修改" : "创建流程"}
        </Button>
      </DialogFooter>
    </div>
  );
}
